import { renderToString } from "react-dom/server";
import type { ErrorEnvelope } from "@avos/foundation";
import { AppShell } from "../shell/AppShell.js";
import { shellStyleTag } from "../styles/stylesheet.js";

/** Options for a server render. */
export interface RenderAppShellOptions {
  /**
   * An optional error envelope (shared foundation contract) to surface in the
   * rendered shell. Optional per `exactOptionalPropertyTypes`.
   */
  readonly error?: ErrorEnvelope;
}

/** Result of a server render. */
export interface RenderAppShellResult {
  /**
   * The server-rendered application markup. Contains the shell's layout and
   * navigation for the matched route (Req 21.1: present in the initial
   * response body before client script runs).
   */
  readonly html: string;
}

/**
 * `renderAppShell` — the SSR entry point for the AVOS Application_Shell.
 *
 * Given a request URL/pathname, it synchronously renders the shell (layout +
 * navigation + the route's screen) to an HTML string using
 * `react-dom/server`'s `renderToString`. The returned `html` is the app markup
 * that a host server embeds into the document body (see
 * {@link renderAppDocument}) and streams as the initial response — so the
 * layout and navigation are in the response body BEFORE any client script runs
 * (Requirement 21.1, NFR-U1).
 *
 * Pure and deterministic: it reads no clock, environment, or global state, so
 * the same URL always yields the same markup. That keeps the server output and
 * the client's hydration input identical (see `client/hydrate.ts`).
 *
 * Later web tasks plug in by adding routes/screens (see `shell/routes.ts` and
 * `screens/screens.tsx`); this entry point does not change.
 */
export function renderAppShell(
  url: string,
  options: RenderAppShellOptions = {},
): RenderAppShellResult {
  const props =
    options.error === undefined ? { url } : { url, error: options.error };
  const html = renderToString(<AppShell {...props} />);
  return { html };
}

/** The DOM id the app mounts into (shared by server markup and client hydration). */
export const APP_ROOT_ID = "app-root";

/**
 * The official AVOS mark as an inline SVG favicon (data URI). Two domes on a
 * shared baseline with the champagne-gold gradient intersection — the same
 * master artwork rendered by {@link ArchMark}, on a transparent background so it
 * sits on any browser-tab colour. URL-encoded for use in a `href`.
 */
const AVOS_FAVICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 79 28">' +
  '<path d="M38.8451 2.97989C45.1589 -0.25416 52.5043 -0.82841 59.2441 1.38519C65.7773 3.46196 71.5204 8.39216 74.6545 14.4771C76.1853 17.3896 77.1486 20.5665 77.4923 23.8386C77.6003 24.7823 77.5936 25.9502 77.7202 26.8155C76.8405 26.7779 75.7842 26.7235 74.9171 26.8243C75.026 20.8199 72.4399 14.5335 68.2874 10.2235C63.9274 5.63057 57.9172 2.96325 51.5851 2.81165C47.6497 2.73923 45.2909 3.31381 41.649 4.61864C47.2101 8.22274 51.4552 14.5036 52.8425 20.9732C53.1046 22.1855 53.2863 23.4136 53.3862 24.6497C53.4409 25.3175 53.4564 26.1927 53.5369 26.8157C52.5709 26.7549 51.6742 26.7735 50.709 26.7866L26.9052 26.8143C25.9868 26.7828 25.0806 26.7492 24.1622 26.823C24.2544 25.4276 24.2869 24.2573 24.5018 22.863C25.4253 16.5342 28.5985 10.7498 33.4402 6.56929C34.1426 5.95512 35.254 5.09919 36.0541 4.58893C34.3024 4.01079 32.8764 3.5142 31.0561 3.17023C25.9056 2.24213 20.5928 3.01863 15.9235 5.38197C8.0811 9.33469 2.50738 17.9469 2.82476 26.8183C1.95266 26.7407 0.876322 26.748 0 26.8146C0.179841 19.337 2.81522 12.6884 8.26854 7.4768C13.3682 2.58112 20.209 -0.0811168 27.2772 0.0791799C30.9846 0.167474 35.591 1.12934 38.8451 2.97989Z" fill="#10233C"/>' +
  '<path d="M27.6489 20.9643C29.241 14.379 33.2342 9.61731 38.8548 6.03711C44.9652 9.98434 48.6264 14.6438 50.263 21.9011C50.4266 22.6271 50.9443 26.3745 50.7082 26.7866L26.9043 26.8145C26.9051 25.6296 27.0581 23.6812 27.3053 22.567C27.3902 21.959 27.494 21.5582 27.6489 20.9643Z" fill="#97A6ED"/>' +
  "</svg>";

/**
 * The favicon SVG packaged as a `data:` URI. The raw SVG contains double-quotes
 * and `<`/`>` in its markup; `encodeURIComponent` percent-encodes all of them
 * so the result is a single well-formed `href` value with no characters that
 * could terminate the surrounding `href="…"` attribute (which previously spilled
 * the SVG markup into the document as a stray `">` text node).
 */
const AVOS_FAVICON_DATA_URI =
  "data:image/svg+xml," + encodeURIComponent(AVOS_FAVICON_SVG);

/** Options for rendering the full HTML document. */
export interface RenderAppDocumentOptions extends RenderAppShellOptions {
  /**
   * URL of the client hydration bundle to load with `type="module"`. The
   * script is emitted at the end of `<body>` and is `defer`-equivalent (module
   * scripts are deferred), so the server-rendered layout/navigation is parsed
   * and displayable before the client script executes (Req 21.1). Optional: a
   * host that has no bundle yet still gets valid, script-free SSR HTML.
   */
  readonly clientScriptUrl?: string;
  /** Document title. */
  readonly title?: string;
  /** Optional web app manifest URL (PWA task wires this — Req 21.2). */
  readonly manifestUrl?: string;
  /**
   * The enabled product-module ids for this environment (resolved server-side
   * from AVOS_ENABLED_MODULES via the central registry), or the string "all".
   * Injected as a tiny inline bootstrap (`window.__AVOS_ENABLED_MODULES__`) so
   * the console navigation can HIDE disabled modules from the SAME source of
   * truth the server uses to GATE them. This is display-only: server-side route
   * + API gating is the real control. Omitted ⇒ the client treats all modules
   * as enabled (safe default; the server still gates).
   */
  readonly enabledModules?: readonly string[] | "all";
}

/**
 * Render a complete HTML document string around the server-rendered shell.
 *
 * This is what a host HTTP handler returns as the initial response body. The
 * order is deliberate: the shell markup (layout + navigation) appears in the
 * body first; the hydration `<script type="module">`, if any, is emitted LAST,
 * so client script runs only after the server-rendered content is present
 * (Req 21.1).
 */
export function renderAppDocument(
  url: string,
  options: RenderAppDocumentOptions = {},
): string {
  const { html } = renderAppShell(url, resolveShellOptions(options));
  const title = options.title ?? "AVOS";
  const manifestLink =
    options.manifestUrl === undefined
      ? ""
      : `<link rel="manifest" href="${escapeAttribute(options.manifestUrl)}">`;
  const clientScript =
    options.clientScriptUrl === undefined
      ? ""
      : `<script type="module" src="${escapeAttribute(options.clientScriptUrl)}"></script>`;
  // Inline module-availability bootstrap. JSON.stringify output is safe inside a
  // script as long as we neutralise the "</" sequence that could close the tag.
  const modulesBootstrap =
    options.enabledModules === undefined
      ? ""
      : `<script>window.__AVOS_ENABLED_MODULES__=${JSON.stringify(options.enabledModules).replace(/</g, "\\u003c")};</script>`;
  return (
    "<!DOCTYPE html>" +
    '<html lang="en">' +
    "<head>" +
    '<meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    `<title>${escapeText(title)}</title>` +
    // Product font (SaaS v2): ONE family — Proxima Nova, served locally from
    // /fonts (licensed .ttf assets). No external Google Fonts request. We
    // PRELOAD the two hot weights (regular + semibold) so first paint swaps to
    // the brand font quickly; the inlined critical CSS @font-faces them and the
    // stack falls back to Inter / system UI until they arrive (Req 21.4).
    '<link rel="preload" href="/fonts/ProximaNova-Regular.ttf" as="font" type="font/ttf" crossorigin>' +
    '<link rel="preload" href="/fonts/ProximaNova-Semibold.ttf" as="font" type="font/ttf" crossorigin>' +
    // Favicon: the official AVOS mark as an inline SVG data URI (the two domes +
    // gold-gradient intersection), so the browser tab / home-screen icon carry
    // the brand. On the dark tab UI the outer arches read as forest-green.
    `<link rel="icon" type="image/svg+xml" href="${AVOS_FAVICON_DATA_URI}">` +
    // Responsive + accessibility scaffolding (Task 14.3, Req 21.3/21.5/21.6):
    // the shell stylesheet is inlined in the head so the server-rendered
    // layout has its responsive container, focus indicator, and skip-link
    // styles applied before any client script runs.
    shellStyleTag() +
    manifestLink +
    "</head>" +
    "<body>" +
    `<div id="${APP_ROOT_ID}">${html}</div>` +
    modulesBootstrap +
    clientScript +
    "</body>" +
    "</html>"
  );
}

/** Narrow document options to just the shell render options. */
function resolveShellOptions(
  options: RenderAppDocumentOptions,
): RenderAppShellOptions {
  return options.error === undefined ? {} : { error: options.error };
}

/** Minimal HTML text escaping for interpolated document strings. */
function escapeText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Minimal HTML attribute escaping for interpolated URLs/values. */
function escapeAttribute(value: string): string {
  return escapeText(value).replace(/"/g, "&quot;");
}
